import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PanelLeft, ChevronDown, LogOut, UserRound } from 'lucide-react';
import { useSidebarCollapse } from '../../contexts/SidebarCollapseContext';
import { routeItemId } from '../../lib/sidebar/navigation';
import { TOP_LEVEL_ITEMS, SIDEBAR_CATEGORIES } from './sidebarConfig';
import { ChatGlocke } from './topbar/ChatGlocke';

interface TopBarProps {
  fullName?: string;
  role?: string;
  email?: string;
  avatarUrl?: string;
  onLogout: () => void;
}

function initialen(name?: string): string {
  const p = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (p.length === 0) return '?';
  if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
  return (p[0][0] + p[p.length - 1][0]).toUpperCase();
}

// Aktueller Bereichsname aus der Route (gleiche Ableitung wie die Sidebar-Hervorhebung).
function bereichLabel(pathname: string): string {
  const id = routeItemId(pathname);
  const alle = [...TOP_LEVEL_ITEMS, ...SIDEBAR_CATEGORIES.flatMap((c) => c.items)];
  return alle.find((i) => i.id === id)?.label ?? '';
}

const TopBar: React.FC<TopBarProps> = ({ fullName, role, email, avatarUrl, onLogout }) => {
  const { toggle } = useSidebarCollapse();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOffen, setMenuOffen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOffen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOffen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onClick); document.removeEventListener('keydown', onKey); };
  }, []);

  const bereich = bereichLabel(location.pathname);

  return (
    <header className="relative z-30 shrink-0 border-b border-white/40 bg-white/30 backdrop-blur-xl pt-[env(safe-area-inset-top)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]">
      <div className="h-[40px] lg:h-[52px] flex items-center justify-between px-3 lg:px-6">
        {/* Links: Einklappen + Bereich (pl-10 auf Mobile, damit der Hamburger nicht kollidiert) */}
        <div className="flex items-center gap-2.5 min-w-0 pl-14 lg:pl-0">
          <button
            onClick={toggle}
            aria-label="Seitenleiste ein-/ausklappen"
            className="hidden lg:flex items-center justify-center w-8 h-8 rounded-lg text-gray-500 hover:text-[#0f172a] hover:bg-white/60 transition-colors"
          >
            <PanelLeft size={17} strokeWidth={1.8} />
          </button>
          {bereich && (
            <span className="text-[12px] font-black uppercase tracking-[0.16em] text-gray-500 truncate">{bereich}</span>
          )}
        </div>

        {/* Rechts: Chat + Profil. Der laufende Ticket-Timer steht in der Pille (Handoff Tickets §7). */}
        <div className="flex shrink-0 items-center gap-1.5 lg:gap-2">
        <ChatGlocke />
        <div className="relative shrink-0" ref={menuRef}>
          <button
            onClick={() => setMenuOffen((o) => !o)}
            className="flex items-center gap-2 rounded-xl pl-1 pr-2 py-1 hover:bg-white/60 transition-colors"
          >
            {avatarUrl ? (
              <img src={avatarUrl} alt="" className="w-8 h-8 rounded-lg object-cover ring-1 ring-white/60" />
            ) : (
              <span className="w-8 h-8 rounded-lg bg-[#1a1d24] text-[#c4a77d] flex items-center justify-center text-[11px] font-black">
                {initialen(fullName)}
              </span>
            )}
            <span className="hidden sm:flex flex-col items-start leading-tight">
              <span className="text-[12px] font-bold text-[#0f172a] truncate max-w-[150px]">{fullName || 'Konto'}</span>
              {role && <span className="text-[9px] font-black uppercase tracking-[0.12em] text-[#c4a77d]">{role}</span>}
            </span>
            <ChevronDown size={14} className={`text-gray-400 transition-transform ${menuOffen ? 'rotate-180' : ''}`} />
          </button>

          {menuOffen && (
            <div className="absolute right-0 top-full mt-2 w-56 rounded-2xl bg-white/80 backdrop-blur-2xl border border-white/50 shadow-[0_16px_44px_-16px_rgba(15,23,42,0.35)] overflow-hidden z-50 animate-in fade-in slide-in-from-top-1 duration-150">
              <div className="px-4 py-3 border-b border-white/50">
                <div className="text-[13px] font-bold text-[#0f172a] truncate">{fullName || 'Konto'}</div>
                {email && <div className="text-[11px] font-semibold text-gray-400 truncate">{email}</div>}
              </div>
              <button
                onClick={() => { setMenuOffen(false); navigate('/admin/profile'); }}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-[12px] font-bold text-gray-600 hover:bg-white/70 hover:text-[#0f172a] transition-colors"
              >
                <UserRound size={15} className="text-[#c4a77d]" /> Profil
              </button>
              <button
                onClick={() => { setMenuOffen(false); onLogout(); }}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-[12px] font-bold text-red-500 hover:bg-red-50/60 transition-colors"
              >
                <LogOut size={15} /> Abmelden
              </button>
            </div>
          )}
        </div>
        </div>
      </div>
    </header>
  );
};

export default TopBar;
